import { useState, useMemo, useEffect } from "react";
import { supabase } from "./supabase";
import { downloadEntrataCsv } from "./entrata";

// ─── CONSTANTS & THEME ───────────────────────────────────────────────────────
const TODAY = new Date().toISOString().slice(0, 7);
const AMORTIQ_TAG = "[AmortIQ]";

const addMonths = (ym, n) => { const [y, m] = ym.split("-").map(Number); const d = new Date(y, m - 1 + n, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };
const monthLabel = (ym) => { const [y, m] = ym.split("-").map(Number); return new Date(y, m - 1, 15).toLocaleDateString("en-US", { month: "short", year: "numeric" }); };
const uid = () => Math.random().toString(36).slice(2, 9);
const fmt$ = (n) => n == null || isNaN(n) ? "$—" : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(n);
const makeTaggedMemo = (jeRef, desc) => `${AMORTIQ_TAG} ${jeRef} — ${desc}`;

// ─── EXTERNAL GL NOISE (simulated Entrata GL pulls) ──────────────────────────
const EXTERNAL_GL_NOISE = [
  { glJeRef: "ENT-83241", glCode: "1500-001", period: "2025-02", amount: -10000, debitCredit: "CR", memo: "Reclass partial insurance to Oakwood property — per mgmt request", postedBy: "M. Chen", postedAt: "2025-02-18T14:22:00Z", source: "manual" },
  { glJeRef: "ENT-84902", glCode: "1500-001", period: "2025-03", amount: -5000, debitCredit: "CR", memo: "Write-down prepaid insurance — policy cancelled early", postedBy: "J. Rodriguez", postedAt: "2025-03-05T09:11:00Z", source: "manual" },
  { glJeRef: "ENT-85510", glCode: "1500-002", period: "2025-06", amount: 3600, debitCredit: "DR", memo: "Additional Entrata module added — prepaid portion", postedBy: "M. Chen", postedAt: "2025-06-12T11:44:00Z", source: "manual" },
  { glJeRef: "ENT-91003", glCode: "1500-003", period: "2025-09", amount: -2400, debitCredit: "CR", memo: "D&O policy endorsement credit — return premium", postedBy: "J. Rodriguez", postedAt: "2025-09-30T16:05:00Z", source: "manual" },
];

// Posting and schedule generation now live inside the component and write
// through to Supabase; the database enforces posting idempotency.

// ─── SEED DATA ───────────────────────────────────────────────────────────────


// ─── CSV EXPORT ──────────────────────────────────────────────────────────────
function downloadCSV(filename, headers, rows) {
  const esc = (c) => `"${String(c ?? "").replace(/"/g, '""')}"`;
  const csv = [headers.join(","), ...rows.map((r) => r.map(esc).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function exportScheduleCSV(entry, ledger) {
  const headers = ["Period", "Debit GL", "Credit GL", "Amount", "Status", "JE Ref", "Batch", "Posted At", "Posted By", "Memo"];
  const rows = entry.schedule.map((r) => {
    const rec = ledger[r.postingKey];
    return [monthLabel(r.period), r.debitGL, r.creditGL, r.amount, rec ? "Posted" : r.period <= TODAY ? "Pending" : "Scheduled", rec?.jeRef || "", rec?.batchId || "", rec?.postedAt?.slice(0, 19) || "", rec?.postedBy || "", rec?.rawMemo || r.memo];
  });
  downloadCSV(`amortiq_${entry.vendor.replace(/\s+/g, "_")}_schedule.csv`, headers, rows);
}

function exportAuditCSV(log) {
  const headers = ["Timestamp", "Action", "Posting Key", "By", "Note"];
  const rows = [...log].reverse().map((r) => [r.ts.slice(0, 19), r.action, r.key, r.by, r.note]);
  downloadCSV("amortiq_audit_log.csv", headers, rows);
}

// ─── STYLES ──────────────────────────────────────────────────────────────────
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600&family=IBM+Plex+Sans:wght@400;500;600;700&family=Fraunces:opsz,wght@9..144,500;9..144,700;9..144,900&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
:root{
  --bg:#F0EEE6;--s1:#FAF9F5;--s2:#F2EFE7;--s3:#E9E5D9;
  --b1:#E3DED0;--b2:#D2CBB8;--b3:#BAB19A;
  --tx:#403D35;--txb:#1A1915;--mu:#6F6A5C;--di:#B7B09E;
  --acc:#C15F3C;--acc2:#A94E30;--accBg:rgba(193,95,60,.07);--accBd:rgba(193,95,60,.35);
  --gold:#8C6D2F;--goldBg:rgba(140,109,47,.08);--goldBd:rgba(140,109,47,.35);
  --green:#2E7D4F;--greenBg:rgba(46,125,79,.08);--greenBd:rgba(46,125,79,.35);
  --red:#B3382C;--redBg:rgba(179,56,44,.06);--redBd:rgba(179,56,44,.35);
  --blue:#3B6FA0;--teal:#2E7D6E;
  --amber:#9A6A00;--amberBg:rgba(154,106,0,.07);--amberBd:rgba(154,106,0,.35);
  --orange:#C2703E;
  --sans:'IBM Plex Sans',-apple-system,'Segoe UI',sans-serif;
  --mono:'IBM Plex Mono',monospace;
  --head:'Fraunces',serif;
}
html,body,#root{height:100%;overflow:hidden}
body{background:var(--bg);color:var(--tx);font-family:var(--sans);font-size:13px;line-height:1.5;-webkit-font-smoothing:antialiased}
input,select,button,textarea{font-family:inherit}

/* Layout */
.shell{display:grid;grid-template-rows:52px 1fr;grid-template-columns:320px 1fr;height:100vh;overflow:hidden}
.hdr{grid-column:1/-1;display:flex;align-items:center;gap:12px;padding:0 20px;border-bottom:1px solid var(--b1);background:var(--s1);z-index:100}
.list-panel{grid-row:2;overflow:hidden;display:flex;flex-direction:column;border-right:1px solid var(--b1);background:var(--s1)}
.main-pane{grid-row:2;overflow-y:auto;overflow-x:hidden;background:var(--bg)}

/* Header */
.logo{font-family:var(--head);font-size:19px;font-weight:900;letter-spacing:-.3px;color:var(--txb);white-space:nowrap;cursor:pointer}
.logo em{color:var(--acc);font-style:normal}
.mod-switch{display:flex;align-items:center;border:1px solid var(--b2);border-radius:7px;overflow:hidden;background:var(--s2)}
.mod{padding:4px 12px;font-size:10.5px;font-weight:600;letter-spacing:.03em;color:var(--mu);text-decoration:none;cursor:pointer;transition:all .12s;line-height:1.4}
.mod.on{background:var(--acc);color:#fff}
.mod:not(.on):hover{background:var(--s3);color:var(--tx)}
.hdr-pills{margin-left:auto;display:flex;align-items:center;gap:8px}
.hpill{font-size:9px;padding:3px 9px;border:1px solid;border-radius:4px;letter-spacing:.1em;text-transform:uppercase;cursor:default;font-weight:600}
.hdr-gear{background:none;border:1px solid var(--b2);border-radius:6px;color:var(--mu);width:30px;height:30px;display:flex;align-items:center;justify-content:center;cursor:pointer;font-size:14px;transition:all .15s}
.hdr-gear:hover{border-color:var(--acc);color:var(--acc)}
.period-sel{background:var(--s1);border:1px solid var(--b2);border-radius:6px;color:var(--acc);font-family:var(--mono);font-size:11px;font-weight:500;padding:4px 8px;cursor:pointer}
.period-sel:focus{border-color:var(--acc);outline:none}

/* List Panel */
.lp-head{padding:12px 16px;border-bottom:1px solid var(--b1);display:flex;align-items:center;justify-content:space-between;flex-shrink:0}
.lp-title{font-size:10px;letter-spacing:.12em;text-transform:uppercase;color:var(--mu);font-weight:600}
.lp-count{font-size:10px;padding:1px 7px;border:1px solid var(--accBd);border-radius:4px;color:var(--acc);margin-left:8px;font-weight:600}
.lp-list{flex:1;overflow-y:auto;padding:4px 0}
.lp-item{padding:10px 16px;cursor:pointer;border-left:2px solid transparent;transition:all .1s;border-bottom:1px solid var(--b1)}
.lp-item:hover{background:var(--s2)}
.lp-item.active{background:var(--s2);border-left-color:var(--acc)}
.lp-item.done{opacity:.55}
.lp-top{display:flex;align-items:center;gap:8px;margin-bottom:3px}
.lp-dot{width:8px;height:8px;border-radius:50%;flex-shrink:0}
.lp-name{font-size:12.5px;font-weight:600;color:var(--txb);flex:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.lp-bal{font-size:11.5px;font-weight:600;color:var(--txb);font-family:var(--mono);font-variant-numeric:tabular-nums}
.lp-meta{font-size:10.5px;color:var(--mu);display:flex;gap:8px}
.lp-bar{height:3px;background:var(--b1);border-radius:99px;margin-top:5px;overflow:hidden}
.lp-bar-fill{height:100%;background:var(--acc)}
.lp-nav{border-top:1px solid var(--b1);flex-shrink:0;padding:8px 12px}
.lp-section{padding:6px 16px 2px;font-size:9px;letter-spacing:.12em;text-transform:uppercase;color:var(--di);border-top:1px solid var(--b1);margin-top:2px}

/* Main Content */
.mp-inner{padding:24px 28px;max-width:1100px}

/* Cards */
.card{background:var(--s1);border:1px solid var(--b1);border-radius:8px;margin-bottom:16px;box-shadow:0 1px 2px rgba(26,25,21,.04);overflow:hidden}
.card-hd{display:flex;align-items:center;justify-content:space-between;padding:10px 16px;border-bottom:1px solid var(--b1)}
.card-title{font-size:10px;letter-spacing:.12em;text-transform:uppercase;color:var(--acc);font-weight:700}
.card-body{padding:14px 16px}
.card-foot{padding:10px 16px;border-top:1px solid var(--b1);display:flex;justify-content:flex-end;gap:8px;background:var(--s2)}

/* Stats */
.stats{display:flex;border:1px solid var(--b1);border-radius:8px;margin-bottom:20px;background:var(--s1);box-shadow:0 1px 2px rgba(26,25,21,.04);overflow:hidden}
.stat{flex:1;padding:14px 16px;border-right:1px solid var(--b1)}
.stat:last-child{border-right:none}
.stat-val{font-family:var(--head);font-size:24px;font-weight:900;letter-spacing:-.5px}
.stat-lbl{font-size:9px;color:var(--mu);letter-spacing:.1em;text-transform:uppercase;margin-top:2px;font-weight:600}

/* Section headers */
.sec-hd{display:flex;align-items:center;gap:10px;margin-bottom:10px;margin-top:4px}
.sec-num{width:19px;height:19px;background:var(--acc);color:#fff;border-radius:5px;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:700;flex-shrink:0}
.sec-label{font-size:10.5px;letter-spacing:.1em;text-transform:uppercase;color:var(--acc);font-weight:700}
.sec-line{flex:1;height:1px;background:var(--b1)}

/* Buttons */
.btn{border:1px solid var(--b2);background:var(--s1);color:var(--tx);font-family:var(--sans);font-size:10.5px;font-weight:600;letter-spacing:.05em;text-transform:uppercase;padding:7px 14px;border-radius:6px;cursor:pointer;transition:all .12s;display:inline-flex;align-items:center;gap:5px;white-space:nowrap}
.btn:hover{border-color:var(--b3);background:var(--s3)}
.btn:disabled{opacity:.45;cursor:not-allowed}
.btn-acc{background:var(--acc);border-color:var(--acc);color:#fff}
.btn-acc:hover{background:var(--acc2);border-color:var(--acc2)}
.btn-ghost{background:none;border-color:var(--b2);color:var(--mu)}
.btn-ghost:hover{border-color:var(--b3);color:var(--tx);background:none}
.btn-danger{background:none;border-color:var(--redBd);color:var(--red)}
.btn-danger:hover{background:var(--redBg)}
.btn-amber{background:var(--amberBg);border-color:var(--amberBd);color:var(--amber)}
.btn-sm{padding:5px 10px;font-size:9.5px}
.btn-xs{padding:3px 8px;font-size:9px}
.btn-block{width:100%;justify-content:center}

/* Form */
.field{display:flex;flex-direction:column;gap:4px;margin-bottom:12px}
.lbl{font-size:9.5px;letter-spacing:.1em;text-transform:uppercase;color:var(--mu);font-weight:600}
.inp,.sel{background:#fff;border:1px solid var(--b2);border-radius:6px;color:var(--txb);font-family:var(--sans);font-size:12.5px;padding:7px 10px;width:100%;outline:none;transition:border .12s}
.inp:focus,.sel:focus{border-color:var(--acc)}
.inp::placeholder{color:var(--di)}
textarea.inp{resize:vertical;min-height:60px}

/* Tables */
.tbl{width:100%;border-collapse:collapse;font-size:12px}
.tbl th{border-bottom:1px solid var(--b2);padding:7px 10px;text-align:left;font-size:9px;letter-spacing:.1em;text-transform:uppercase;color:var(--mu);background:var(--s2);white-space:nowrap;font-weight:600}
.tbl td{padding:7px 10px;border-bottom:1px solid var(--b1);vertical-align:middle;font-variant-numeric:tabular-nums}
.tbl tr:last-child td{border-bottom:none}
.tbl tr:hover td{background:rgba(26,25,21,.02)}

/* Badges */
.badge{font-size:8.5px;padding:2px 7px;border:1px solid;border-radius:4px;letter-spacing:.07em;text-transform:uppercase;white-space:nowrap;display:inline-block;font-weight:600}
.b-green{border-color:var(--greenBd);color:var(--green)}
.b-gold{border-color:var(--goldBd);color:var(--gold)}
.b-amber{border-color:var(--amberBd);color:var(--amber)}
.b-red{border-color:var(--redBd);color:var(--red)}
.b-blue{border-color:rgba(59,111,160,.35);color:var(--blue)}
.b-dim{border-color:var(--b2);color:var(--mu)}
.b-teal{border-color:var(--accBd);color:var(--acc)}
.b-orange{border-color:rgba(194,112,62,.4);color:var(--orange)}

/* Progress bar */
.prog{height:4px;background:var(--b1);border-radius:99px;width:100%;overflow:hidden}
.prog-fill{height:100%;background:var(--acc)}

/* Schedule table zones */
.zone{margin-bottom:12px}
.zone-hd{display:flex;align-items:center;gap:8px;padding:7px 10px;border:1px solid var(--b1);border-radius:6px 6px 0 0;background:var(--s2);cursor:pointer;transition:background .1s}
.zone-hd:hover{background:var(--s3)}
.zone-label{font-size:9.5px;letter-spacing:.1em;text-transform:uppercase;font-weight:700}
.zone-count{font-size:10.5px;color:var(--mu);margin-left:auto;font-family:var(--mono);font-variant-numeric:tabular-nums}
.zone-body{border:1px solid var(--b1);border-top:none;border-radius:0 0 6px 6px;background:var(--s1);overflow:hidden}
.zone-action .zone-hd{border-color:var(--accBd);background:var(--accBg)}
.zone-behind .zone-hd{border-color:var(--redBd);background:var(--redBg)}
.zone-past{opacity:.72}
.zone-future{opacity:.62}

/* Drawer */
.drawer-overlay{position:fixed;inset:0;background:rgba(26,25,21,.3);z-index:500;opacity:0;pointer-events:none;transition:opacity .2s}
.drawer-overlay.open{opacity:1;pointer-events:auto}
.drawer{position:fixed;top:0;right:0;bottom:0;width:640px;max-width:92vw;background:var(--s1);border-left:1px solid var(--b1);box-shadow:-8px 0 28px rgba(26,25,21,.1);z-index:501;transform:translateX(100%);transition:transform .25s ease;overflow-y:auto}
.drawer.open{transform:translateX(0)}
.drawer-hd{display:flex;align-items:center;justify-content:space-between;padding:14px 20px;border-bottom:1px solid var(--b1);position:sticky;top:0;background:var(--s1);z-index:1}
.drawer-title{font-family:var(--head);font-size:17px;font-weight:700;color:var(--txb)}
.drawer-tabs{display:flex;gap:0;border-bottom:1px solid var(--b1);padding:0 20px;position:sticky;top:53px;background:var(--s1);z-index:1}
.dtab{background:none;border:none;color:var(--mu);font-family:var(--sans);font-size:10px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;padding:9px 14px;cursor:pointer;border-bottom:2px solid transparent;margin-bottom:-1px;transition:all .12s}
.dtab:hover{color:var(--tx)}
.dtab.on{color:var(--acc);border-bottom-color:var(--acc)}
.drawer-body{padding:20px}

/* Confirm Modal */
.modal-overlay{position:fixed;inset:0;background:rgba(26,25,21,.38);z-index:600;display:flex;align-items:center;justify-content:center}
.modal{background:var(--s1);border:1px solid var(--b1);border-radius:10px;box-shadow:0 12px 40px rgba(26,25,21,.18);padding:20px 24px;max-width:460px;width:90%}

/* Batch result inline */
.batch-result{border:1px solid var(--accBd);border-radius:8px;background:var(--accBg);padding:12px 16px;margin-bottom:16px}
.br-summary{display:flex;align-items:center;gap:10px;margin-bottom:6px}
.br-detail{margin-top:8px;max-height:200px;overflow-y:auto}

/* Recon panels */
.recon-panel{border:1px solid var(--b1);border-radius:8px;background:var(--s1);margin-bottom:12px;overflow:hidden}
.recon-hd{padding:9px 14px;border-bottom:1px solid var(--b1);display:flex;align-items:center;gap:8px;font-size:11.5px;font-weight:600;background:var(--s2)}
.recon-body{max-height:280px;overflow-y:auto}
.recon-row{padding:8px 12px;border-bottom:1px solid var(--b1);font-size:10.5px}
.recon-row:last-child{border-bottom:none}
.recon-memo{margin-top:4px;padding:3px 8px;font-size:9.5px;border-left:2px solid;font-family:var(--mono)}

/* Inline confirm */
.inline-ok{padding:3px 8px;font-size:9px;color:var(--green);border:1px solid var(--greenBd);border-radius:4px;background:var(--greenBg);letter-spacing:.07em;text-transform:uppercase;font-weight:600}

/* Flash */
.flash{position:fixed;bottom:16px;right:16px;background:var(--txb);border:1px solid var(--txb);border-radius:8px;color:var(--s1);padding:9px 15px;font-size:11.5px;font-weight:500;box-shadow:0 6px 20px rgba(26,25,21,.2);z-index:9999;animation:fu .2s ease}
@keyframes fu{from{transform:translateY(8px);opacity:0}to{transform:translateY(0);opacity:1}}

/* Close btn */
.close-btn{background:none;border:1px solid var(--b2);border-radius:6px;color:var(--mu);width:26px;height:26px;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:13px;transition:all .12s}
.close-btn:hover{border-color:var(--red);color:var(--red)}

/* Misc */
hr{border:none;border-top:1px solid var(--b1);margin:10px 0}
.g2{display:grid;grid-template-columns:1fr 1fr;gap:12px}
.g3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px}
.scroll-x{overflow-x:auto}
.empty{text-align:center;padding:36px 20px;color:var(--mu)}
.empty-ico{font-size:20px;margin-bottom:6px;opacity:.5}
.kv-strip{display:flex;flex-wrap:wrap;gap:8px;margin-bottom:12px}
.kv{padding:6px 11px;border:1px solid var(--b1);border-radius:6px;background:var(--s1);min-width:80px}
.kv-label{font-size:8.5px;color:var(--mu);letter-spacing:.09em;text-transform:uppercase;margin-bottom:1px;font-weight:600}
.kv-val{font-size:12.5px;font-weight:600;color:var(--txb);font-family:var(--mono);font-variant-numeric:tabular-nums}
.info-box{background:var(--accBg);border:1px solid var(--accBd);border-radius:8px;padding:9px 13px;margin-bottom:12px;font-size:11.5px;color:var(--acc2);line-height:1.5}
.warn-box{background:var(--amberBg);border:1px solid var(--amberBd);border-radius:8px;padding:10px 14px;margin-bottom:14px;font-size:11.5px;color:var(--amber);line-height:1.6}

/* Entity Recon Grid */
.pr-row{border:1px solid var(--b1);border-radius:6px;background:var(--s1);margin-bottom:3px;transition:all .12s;overflow:hidden}
.pr-row:hover{border-color:var(--b3)}
.pr-row.expanded{border-color:var(--accBd);margin-bottom:8px;box-shadow:0 2px 8px rgba(26,25,21,.06)}
.pr-header{display:grid;grid-template-columns:1fr 100px 120px 120px 120px 60px;align-items:center;padding:10px 16px;cursor:pointer;gap:8px}
.pr-name{font-size:12.5px;font-weight:600;color:var(--txb);display:flex;align-items:center;gap:8px}
.pr-name .pr-arrow{font-size:8px;color:var(--mu);transition:transform .15s;width:12px}
.pr-row.expanded .pr-arrow{transform:rotate(90deg);color:var(--acc)}
.pr-val{font-size:12.5px;font-weight:600;text-align:right;font-family:var(--mono);font-variant-numeric:tabular-nums}
.pr-pct{font-size:10.5px;color:var(--mu);text-align:right;font-family:var(--mono)}
.pr-bar{height:3px;background:var(--b1);margin:0 16px;border-radius:99px;overflow:hidden}
.pr-bar-fill{height:100%;background:var(--acc);transition:width .2s}
.pr-detail{border-top:1px solid var(--b1);padding:0}
.pr-detail .tbl td{font-size:11px}
.pr-detail .tbl th{font-size:8.5px}
.pr-filter{display:flex;align-items:center;gap:8px;margin-bottom:14px}
.pr-search{background:#fff;border:1px solid var(--b2);border-radius:6px;color:var(--txb);font-family:var(--sans);font-size:11.5px;padding:6px 11px;width:220px;outline:none;transition:border .12s}
.pr-search:focus{border-color:var(--acc)}
.pr-search::placeholder{color:var(--di)}
.pr-totals{display:grid;grid-template-columns:1fr 100px 120px 120px 120px 60px;align-items:center;padding:9px 16px;gap:8px;border-top:2px solid var(--b2);background:var(--s2);font-weight:700;border-radius:0 0 6px 6px}

::-webkit-scrollbar{width:8px;height:8px}
::-webkit-scrollbar-track{background:transparent}
::-webkit-scrollbar-thumb{background:var(--b2);border-radius:99px}
::-webkit-scrollbar-thumb:hover{background:var(--b3)}

@media(max-width:900px){
  .shell{grid-template-columns:1fr}
  .list-panel{display:none}
  .stats{flex-wrap:wrap}
  .kv-strip{gap:4px}
  .pr-header{grid-template-columns:1fr 80px 100px 100px 100px 50px;font-size:10px;padding:8px 10px}
  .pr-totals{grid-template-columns:1fr 80px 100px 100px 100px 50px;font-size:10px;padding:6px 10px}
}
`;

// ─── NEW ENTRY FORM (own component — hooks must not run conditionally) ──────
function NewEntryForm({ prefill, onSave, onCancel, glAccounts, entities }) {
  const [f, setF] = useState(prefill || { vendor: "", description: "", totalCost: "", startDate: "", months: "", prepaidGL: "", expenseGL: "", property: "", invoiceRef: "", enteredBy: "" });
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const ready = f.vendor && f.totalCost && f.startDate && f.months && f.prepaidGL && f.expenseGL;
  const monthly = f.totalCost && f.months ? parseFloat(f.totalCost) / parseInt(f.months) : null;
  const glPrepaid = glAccounts.filter((g) => g.category === "prepaid_asset").map((g) => ({ v: g.code, l: `${g.code} · ${g.name}` }));
  const glExpense = glAccounts.filter((g) => g.category === "expense").map((g) => ({ v: g.code, l: `${g.code} · ${g.name}` }));

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <span style={{ fontFamily: "var(--head)", fontSize: 17, fontWeight: 700, color: "var(--txb)" }}>
          {prefill ? "Duplicate Schedule" : "New Prepaid Entry"}
        </span>
        <button className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
      </div>
      <div className="info-box">
        JE memos will carry <strong>{AMORTIQ_TAG}</strong> prefix — distinguishable from manual entries in Entrata GL.
      </div>
      <div className="card">
        <div className="card-body">
          <div className="sec-hd"><div className="sec-num">1</div><span className="sec-label">What is this?</span><div className="sec-line" /></div>
          <div className="g2">
            <div className="field"><label className="lbl">Vendor / Payee *</label><input className="inp" value={f.vendor} onChange={(e) => set("vendor")(e.target.value)} placeholder="Travelers Insurance" /></div>
            <div className="field"><label className="lbl">Description *</label><input className="inp" value={f.description} onChange={(e) => set("description")(e.target.value)} placeholder="Property Insurance — Oakwood" /></div>
            <div className="field"><label className="lbl">Invoice / JE Ref</label><input className="inp" value={f.invoiceRef} onChange={(e) => set("invoiceRef")(e.target.value)} placeholder="INV-2025-XXXX" /></div>
            <div className="field"><label className="lbl">Entity</label>
              <select className="sel" value={f.property} onChange={(e) => set("property")(e.target.value)}>
                <option value="">— Select —</option>
                {entities.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
              </select>
            </div>
          </div>

          <div className="sec-hd" style={{ marginTop: 6 }}><div className="sec-num">2</div><span className="sec-label">Schedule</span><div className="sec-line" /></div>
          <div className="g3">
            <div className="field"><label className="lbl">Total Cost ($) *</label><input className="inp" type="number" min="0" step="0.01" value={f.totalCost} onChange={(e) => set("totalCost")(e.target.value)} /></div>
            <div className="field"><label className="lbl">Start Month *</label><input className="inp" type="month" value={f.startDate} onChange={(e) => set("startDate")(e.target.value)} /></div>
            <div className="field"><label className="lbl">Months *</label><input className="inp" type="number" min="1" value={f.months} onChange={(e) => set("months")(e.target.value)} /></div>
          </div>

          <div className="sec-hd" style={{ marginTop: 6 }}><div className="sec-num">3</div><span className="sec-label">GL Accounts</span><div className="sec-line" /></div>
          <div className="g2">
            <div className="field"><label className="lbl">Prepaid Asset GL (Credit) *</label>
              <select className="sel" value={f.prepaidGL} onChange={(e) => set("prepaidGL")(e.target.value)}>
                <option value="">— Select —</option>
                {glPrepaid.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </div>
            <div className="field"><label className="lbl">Expense GL (Debit) *</label>
              <select className="sel" value={f.expenseGL} onChange={(e) => set("expenseGL")(e.target.value)}>
                <option value="">— Select —</option>
                {glExpense.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </div>
            <div className="field"><label className="lbl">Entered By</label><input className="inp" value={f.enteredBy} onChange={(e) => set("enteredBy")(e.target.value)} placeholder="Your name" /></div>
          </div>

          {monthly && (
            <div style={{ padding: "8px 12px", border: "1px solid var(--accBd)", background: "var(--accBg)", borderRadius: 8, marginTop: 8, fontSize: 11.5, color: "var(--acc2)" }}>
              Monthly: <strong>{fmt$(monthly)}</strong> · {f.months} periods · Total: <strong>{fmt$(parseFloat(f.totalCost))}</strong>
            </div>
          )}
        </div>
        <div className="card-foot">
          <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <button className="btn btn-acc" disabled={!ready} onClick={() => onSave(f)}>Save & Generate Schedule</button>
        </div>
      </div>
    </>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function AmortIQ({ auth }) {
  const { profile, org, signOut } = auth;
  const [entries, setEntries] = useState([]);
  const [ledger, setLedger] = useState({});
  const [auditLog, setAuditLog] = useState([]);
  const [glAccounts, setGlAccounts] = useState([]);
  const [entities, setEntities] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [closePeriod, setClosePeriod] = useState(org.settings?.close_period || TODAY);
  const sampleMode = !!org.settings?.sample_data;
  const NOISE = sampleMode ? EXTERNAL_GL_NOISE : [];

  // UI state
  const [selectedId, setSelectedId] = useState(null);
  const [rightView, setRightView] = useState("summary"); // summary | detail | newEntry
  const [flash, setFlash] = useState(null);
  const [batchResult, setBatchResult] = useState(null);
  const [recentlyPosted, setRecentlyPosted] = useState(new Set());
  const [showPastZone, setShowPastZone] = useState(false);
  const [showFutureZone, setShowFutureZone] = useState(true);

  // Drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState("recon");
  const [drawerGL, setDrawerGL] = useState("1500-001");

  // Reversal modal
  const [revTarget, setRevTarget] = useState(null);
  const [revReason, setRevReason] = useState("");

  // New entry form (for duplicate prefill)
  const [prefill, setPrefill] = useState(null);

  // Setup tab forms
  const [setupEnt, setSetupEnt] = useState({ name: "", type: "property" });
  const [setupGl, setSetupGl] = useState({ code: "", name: "", category: "prepaid_asset" });

  // Property recon state
  const [expandedProp, setExpandedProp] = useState(null);
  const [propFilter, setPropFilter] = useState("");

  function msg(m) { setFlash(m); setTimeout(() => setFlash(null), 2800); }

  // ── Data loading (Supabase → the client shapes the UI already uses) ───────
  async function loadAll() {
    const [ents, gls, scheds, posts, audits] = await Promise.all([
      supabase.from("entities").select("*").eq("org_id", org.id).order("name"),
      supabase.from("gl_accounts").select("*").eq("org_id", org.id).order("code"),
      supabase.from("amort_schedules").select("*, amort_schedule_lines(*)").eq("org_id", org.id).order("created_at", { ascending: false }),
      supabase.from("amort_postings").select("*").eq("org_id", org.id).is("reversed_at", null),
      supabase.from("audit_log").select("*").eq("org_id", org.id).eq("module", "amortiq").order("created_at", { ascending: true }).limit(1000),
    ]);
    const err = ents.error || gls.error || scheds.error || posts.error || audits.error;
    if (err) { msg(`Load failed: ${err.message}`); return; }
    const glById = Object.fromEntries(gls.data.map((g) => [g.id, g]));
    const entById = Object.fromEntries(ents.data.map((e) => [e.id, e]));
    const names = { [profile.id]: profile.display_name || profile.email };
    const newEntries = scheds.data.map((s) => {
      const pCode = glById[s.prepaid_gl_id]?.code || "?";
      const eCode = glById[s.expense_gl_id]?.code || "?";
      const lines = [...(s.amort_schedule_lines || [])].sort((a, b) => a.seq - b.seq);
      return {
        id: s.id, vendor: s.vendor, description: s.description, invoiceRef: s.invoice_ref,
        totalCost: +s.total_cost, startDate: s.start_period.slice(0, 7), months: s.months,
        prepaidGL: pCode, expenseGL: eCode,
        property: entById[s.entity_id]?.name || "Unassigned",
        enteredBy: names[s.entered_by] || "—", status: s.status,
        schedule: lines.map((l) => ({
          rowId: l.id, postingKey: l.id, period: l.period.slice(0, 7), amount: +l.amount,
          debitGL: eCode, creditGL: pCode,
          memo: `Amortize — ${s.description} (${s.vendor}) — ${l.seq}/${s.months}`,
        })),
      };
    });
    const lineMeta = {};
    newEntries.forEach((e) => e.schedule.forEach((r) => { lineMeta[r.postingKey] = { entryId: e.id, row: r }; }));
    const newLedger = {};
    posts.data.forEach((p) => {
      const m = lineMeta[p.line_id];
      if (!m) return;
      newLedger[p.line_id] = {
        key: p.line_id, postingId: p.id, entryId: m.entryId, period: m.row.period, amount: m.row.amount,
        debitGL: m.row.debitGL, creditGL: m.row.creditGL, memo: p.memo, rawMemo: m.row.memo,
        postedAt: p.posted_at, postedBy: names[p.posted_by] || "—", source: p.source,
        jeRef: p.je_ref, batchId: p.batch_id,
      };
    });
    setEntities(ents.data);
    setGlAccounts(gls.data);
    setEntries(newEntries);
    setLedger(newLedger);
    setAuditLog(audits.data.map((a) => ({ ts: a.created_at, action: a.action, key: a.ref || "", by: names[a.actor_id] || "—", note: a.note || "" })));
    setLoaded(true);
  }

  useEffect(() => { loadAll(); }, [org.id]);

  // ── Derived data ──────────────────────────────────────────────────────────
  const selectedEntry = entries.find((e) => e.id === selectedId);

  const entryStats = useMemo(() => entries.map((e) => {
    const posted = e.schedule.filter((r) => ledger[r.postingKey]).reduce((a, r) => a + r.amount, 0);
    const remaining = e.totalCost - posted;
    const pct = Math.round((posted / e.totalCost) * 100);
    const hasBehind = e.schedule.some((r) => r.period <= closePeriod && r.period < closePeriod && !ledger[r.postingKey]);
    const hasCurrent = e.schedule.some((r) => r.period === closePeriod && !ledger[r.postingKey]);
    const currentAmt = e.schedule.find((r) => r.period === closePeriod)?.amount || 0;
    const currentPosted = e.schedule.some((r) => r.period === closePeriod && ledger[r.postingKey]);
    const fullyAmortized = pct >= 100;
    let status = "current";
    if (fullyAmortized) status = "done";
    else if (hasBehind) status = "behind";
    else if (hasCurrent) status = "pending";
    else if (currentPosted) status = "posted";
    return { ...e, posted, remaining, pct, status, hasCurrent, hasBehind, currentAmt, currentPosted, fullyAmortized };
  }), [entries, ledger, closePeriod]);

  const sortedStats = useMemo(() => {
    const order = { behind: 0, pending: 1, current: 2, posted: 3, done: 4 };
    return [...entryStats].sort((a, b) => (order[a.status] ?? 5) - (order[b.status] ?? 5));
  }, [entryStats]);

  const totalPendingNow = entryStats.reduce((a, e) => a + (e.hasCurrent && !e.currentPosted ? e.currentAmt : 0), 0);
  const totalBehind = entryStats.filter((e) => e.hasBehind).length;
  const totalBalance = entryStats.reduce((a, e) => a + e.remaining, 0);
  const pendingCount = entryStats.filter((e) => e.status === "pending" || e.status === "behind").length;
  const postedThisPeriod = entryStats.filter((e) => e.currentPosted).length;
  const externalNoiseCount = NOISE.length;

  // ── Property Reconciliation Data ──────────────────────────────────────────
  const propertyRecon = useMemo(() => {
    const propMap = {};

    entryStats.forEach((e) => {
      const key = e.property || "Unassigned";
      if (!propMap[key]) {
        propMap[key] = { name: key, entries: [], totalCost: 0, amortized: 0, remaining: 0, monthlyRate: 0, scheduleCount: 0, glCodes: new Set() };
      }
      propMap[key].entries.push(e);
      propMap[key].totalCost += e.totalCost;
      propMap[key].amortized += e.posted;
      propMap[key].remaining += e.remaining;
      propMap[key].monthlyRate += e.totalCost / e.months;
      propMap[key].scheduleCount += 1;
      propMap[key].glCodes.add(e.prepaidGL);
    });

    return Object.values(propMap)
      .map((p) => ({ ...p, pct: p.totalCost > 0 ? Math.round((p.amortized / p.totalCost) * 100) : 0 }))
      .sort((a, b) => b.remaining - a.remaining);
  }, [entryStats]);

  const totalOriginalCost = entryStats.reduce((a, e) => a + e.totalCost, 0);
  const totalAmortized = entryStats.reduce((a, e) => a + e.posted, 0);
  const totalMonthlyRate = entryStats.reduce((a, e) => a + e.totalCost / e.months, 0);

  const filteredRecon = useMemo(() => {
    if (!propFilter.trim()) return propertyRecon;
    const q = propFilter.toLowerCase();
    return propertyRecon.filter((p) => p.name.toLowerCase().includes(q));
  }, [propertyRecon, propFilter]);

  // ── Actions (write through to Supabase, then reload) ─────────────────────
  async function postKeys(keys, source) {
    const batchId = `BATCH-${uid().toUpperCase()}`;
    const lineMeta = {};
    entries.forEach((e) => e.schedule.forEach((r) => { lineMeta[r.postingKey] = r; }));
    const posted = [], skipped = [], errors = [];
    const rows = [];
    for (const key of keys) {
      if (ledger[key]) { skipped.push({ key, reason: "Already in posting ledger" }); continue; }
      const row = lineMeta[key];
      if (!row) { errors.push({ key, reason: "Key not found in any schedule" }); continue; }
      if (row.period > closePeriod) { errors.push({ key, reason: `Period ${row.period} is after close period ${closePeriod}` }); continue; }
      const jeRef = `JE-${Math.floor(Math.random() * 90000 + 10000)}`;
      rows.push({ row, db: { org_id: org.id, line_id: key, je_ref: jeRef, batch_id: batchId, memo: makeTaggedMemo(jeRef, row.memo), source, posted_by: profile.id } });
    }
    if (rows.length) {
      const { error } = await supabase.from("amort_postings").insert(rows.map((r) => r.db));
      if (error) {
        // Batch hit a conflict or failure — retry row-by-row so one duplicate
        // doesn't sink the rest. The DB unique index is the real guard.
        for (const r of rows) {
          const { error: e2 } = await supabase.from("amort_postings").insert(r.db);
          if (e2) skipped.push({ key: r.db.line_id, reason: e2.code === "23505" ? "Already posted — blocked by database" : e2.message });
          else posted.push({ key: r.db.line_id, jeRef: r.db.je_ref, period: r.row.period, amount: r.row.amount, debitGL: r.row.debitGL, creditGL: r.row.creditGL });
        }
      } else {
        rows.forEach((r) => posted.push({ key: r.db.line_id, jeRef: r.db.je_ref, period: r.row.period, amount: r.row.amount, debitGL: r.row.debitGL, creditGL: r.row.creditGL }));
      }
      if (posted.length) {
        await supabase.from("audit_log").insert(posted.map((p) => ({ org_id: org.id, module: "amortiq", action: "POST", ref: p.key, actor_id: profile.id, note: `${p.jeRef} · ${source} · ${batchId}` })));
      }
    }
    setBatchResult({ posted, skipped, errors, batchId });
    if (posted.length > 0) {
      setRecentlyPosted(new Set(posted.map((r) => r.key)));
      setTimeout(() => setRecentlyPosted(new Set()), 3000);
    }
    await loadAll();
  }

  function changeClosePeriod(v) {
    setClosePeriod(v);
    supabase.from("organizations").update({ settings: { ...(org.settings || {}), close_period: v } }).eq("id", org.id)
      .then(({ error }) => { if (error) msg(`Could not save close period: ${error.message}`); else org.settings = { ...(org.settings || {}), close_period: v }; });
  }

  async function addEntity(name, entityType) {
    const { error } = await supabase.from("entities").insert({ org_id: org.id, name: name.trim(), entity_type: entityType });
    if (error) { msg(`Could not add entity: ${error.message}`); return false; }
    msg(`${name.trim()} added`);
    await loadAll();
    return true;
  }

  async function addGlAccount(code, name, category) {
    const { error } = await supabase.from("gl_accounts").insert({ org_id: org.id, code: code.trim(), name: name.trim(), category });
    if (error) { msg(`Could not add GL account: ${error.message}`); return false; }
    msg(`${code.trim()} added`);
    await loadAll();
    return true;
  }

  // ── Entrata JE export (Entrata's Import Journal Entries format) ───────────
  function exportEntrataRecs(recs, filename, referenceBase) {
    const entryById = Object.fromEntries(entries.map((e) => [e.id, e]));
    const today = new Date().toISOString().slice(0, 10);
    const byPeriod = {};
    recs.forEach((r) => { (byPeriod[r.period] = byPeriod[r.period] || []).push(r); });
    const journals = Object.keys(byPeriod).sort().map((period) => ({
      reference: `${referenceBase}-${period}`,
      memo: `AmortIQ prepaid amortization — ${monthLabel(period)}`,
      postMonth: period,
      transactionDate: today,
      lines: byPeriod[period].flatMap((r) => {
        const prop = entryById[r.entryId]?.property || "Unassigned";
        return [
          { property: prop, account: r.debitGL, description: r.memo, debit: r.amount, credit: null },
          { property: prop, account: r.creditGL, description: r.memo, debit: null, credit: r.amount },
        ];
      }),
    }));
    downloadEntrataCsv(filename, journals);
    msg(`Entrata file downloaded — ${recs.length} postings`);
  }

  function exportEntrataPeriod() {
    const recs = Object.values(ledger).filter((r) => r.period === closePeriod);
    if (!recs.length) { msg(`Nothing posted in ${monthLabel(closePeriod)} yet — post first, then export`); return; }
    exportEntrataRecs(recs, `AmortIQ_Entrata_JE_${closePeriod}.csv`, "AMORTIQ");
  }

  function exportEntrataBatch(batch) {
    const recs = batch.posted.map((p) => ledger[p.key]).filter(Boolean);
    if (!recs.length) { msg("Nothing in this batch to export"); return; }
    exportEntrataRecs(recs, `AmortIQ_Entrata_${batch.batchId}.csv`, "AMORTIQ");
  }

  function postAllPending() {
    const keys = entries.flatMap((e) => e.schedule.filter((r) => r.period <= closePeriod && !ledger[r.postingKey]).map((r) => r.postingKey));
    postKeys(keys, "batch:all-pending");
  }

  function postSingle(key) { postKeys([key], "manual:single"); }

  function postEntryAll(id) {
    const e = entries.find((x) => x.id === id);
    const keys = e?.schedule.filter((r) => r.period <= closePeriod && !ledger[r.postingKey]).map((r) => r.postingKey) || [];
    postKeys(keys, "batch:entry");
  }

  async function confirmReversal() {
    if (!revTarget || !revReason.trim()) return;
    const revJE = `REV-${Math.floor(Math.random() * 90000 + 10000)}`;
    const { error } = await supabase.from("amort_postings")
      .update({ reversed_at: new Date().toISOString(), reversal_reason: revReason, reversal_je_ref: revJE })
      .eq("id", revTarget.postingId);
    if (error) { msg(`Reversal failed: ${error.message}`); return; }
    await supabase.from("audit_log").insert({ org_id: org.id, module: "amortiq", action: "REVERSE", ref: revTarget.key, actor_id: profile.id, note: `Reversed ${revTarget.jeRef} → ${revJE}. ${revReason}` });
    msg(`${revTarget.jeRef} reversed`);
    setRevTarget(null); setRevReason("");
    await loadAll();
  }

  async function addEntry(f) {
    const glByCode = Object.fromEntries(glAccounts.map((g) => [g.code, g.id]));
    const entByName = Object.fromEntries(entities.map((e) => [e.name, e.id]));
    const months = parseInt(f.months);
    const totalCost = parseFloat(f.totalCost);
    const { data: sched, error } = await supabase.from("amort_schedules").insert({
      org_id: org.id, entity_id: entByName[f.property] || null, vendor: f.vendor, description: f.description,
      invoice_ref: f.invoiceRef || null, total_cost: totalCost, start_period: `${f.startDate}-01`, months,
      prepaid_gl_id: glByCode[f.prepaidGL], expense_gl_id: glByCode[f.expenseGL], entered_by: profile.id,
    }).select("id").single();
    if (error) { msg(`Save failed: ${error.message}`); return; }
    const base = Math.floor((totalCost / months) * 100) / 100;
    const last = +(totalCost - base * (months - 1)).toFixed(2);
    const lineRows = Array.from({ length: months }, (_, i) => ({
      org_id: org.id, schedule_id: sched.id, seq: i + 1,
      period: `${addMonths(f.startDate, i)}-01`, amount: i === months - 1 ? last : base,
    }));
    const { error: le } = await supabase.from("amort_schedule_lines").insert(lineRows);
    if (le) { msg(`Save failed: ${le.message}`); return; }
    await supabase.from("audit_log").insert({ org_id: org.id, module: "amortiq", action: "CREATE", ref: sched.id, actor_id: profile.id, note: `${f.description} (${f.vendor}) · ${fmt$(totalCost)} / ${months} mo` });
    setRightView("summary"); setPrefill(null);
    msg(`${f.description} added`);
    await loadAll();
  }

  function duplicateEntry(e) {
    setPrefill({ vendor: e.vendor, description: e.description + " (Copy)", totalCost: e.totalCost.toString(), months: e.months.toString(), prepaidGL: e.prepaidGL, expenseGL: e.expenseGL, property: e.property, invoiceRef: "", enteredBy: e.enteredBy });
    setSelectedId(null); setRightView("newEntry");
  }

  function openReconForEntry(e) {
    setDrawerGL(e.prepaidGL);
    setDrawerTab("recon");
    setDrawerOpen(true);
  }

  const DOT_COLORS = { behind: "var(--red)", pending: "var(--amber)", posted: "var(--green)", current: "var(--acc)", done: "var(--di)" };

  // ── Schedule zone helpers ─────────────────────────────────────────────────
  function getZones(entry) {
    const behind = entry.schedule.filter((r) => r.period < closePeriod && !ledger[r.postingKey]);
    const current = entry.schedule.filter((r) => r.period === closePeriod);
    const pastPosted = entry.schedule.filter((r) => r.period < closePeriod && ledger[r.postingKey]);
    const future = entry.schedule.filter((r) => r.period > closePeriod);
    return { behind, current, pastPosted, future };
  }

  if (!loaded) {
    return (
      <>
        <style>{CSS}</style>
        <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg)", color: "var(--mu)", fontSize: 13 }}>
          Loading your workspace…
        </div>
      </>
    );
  }

  return (
    <>
      <style>{CSS}</style>
      <div className="shell">

        {/* ════════ HEADER ════════ */}
        <header className="hdr">
          <div className="logo" title="Home" onClick={() => { setSelectedId(null); setRightView("summary"); setBatchResult(null); }}>Amort<em>IQ</em></div>
          <div className="mod-switch" title="PIQ Platform modules">
            <span className="mod on">AmortIQ</span>
            <a className="mod" href="https://www.intercoiq.io">IntercoIQ</a>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: 16 }}>
            <span style={{ fontSize: 8, color: "var(--mu)", letterSpacing: ".1em", textTransform: "uppercase" }}>Close Period</span>
            <input type="month" className="period-sel" value={closePeriod} onChange={(e) => changeClosePeriod(e.target.value)} />
          </div>
          <div className="hdr-pills">
            {pendingCount > 0 && <span className="hpill" style={{ borderColor: "var(--amberBd)", color: "var(--amber)" }}>{pendingCount} pending</span>}
            {totalBehind > 0 && <span className="hpill" style={{ borderColor: "var(--redBd)", color: "var(--red)" }}>{totalBehind} behind</span>}
            {externalNoiseCount > 0 && <span className="hpill" style={{ borderColor: "var(--amberBd)", color: "var(--amber)", cursor: "pointer" }} onClick={() => { setDrawerTab("recon"); setDrawerOpen(true); }}>{externalNoiseCount} GL noise</span>}
            {pendingCount > 0 && <button className="btn btn-acc btn-sm" onClick={postAllPending}>Post All Pending</button>}
            <span className="hpill" style={{ borderColor: "var(--b2)", color: "var(--mu)", textTransform: "none", letterSpacing: 0 }} title={profile.email}>{org.name}</span>
            <button className="btn btn-ghost btn-sm" onClick={signOut}>Sign out</button>
            <button className="hdr-gear" onClick={() => setDrawerOpen(true)} title="GL Recon, Audit Log & Setup">⚙</button>
          </div>
        </header>

        {/* ════════ LEFT PANEL — SCHEDULE LIST ════════ */}
        <div className="list-panel">
          <div className="lp-head">
            <div style={{ display: "flex", alignItems: "center" }}>
              <span className="lp-title">Schedules</span>
              <span className="lp-count">{entries.length}</span>
            </div>
          </div>
          <div className="lp-list">
            {sortedStats.map((e) => (
              <div key={e.id} className={`lp-item ${selectedId === e.id ? "active" : ""} ${e.fullyAmortized ? "done" : ""}`}
                onClick={() => { setSelectedId(e.id); setRightView("detail"); setBatchResult(null); setShowPastZone(false); }}>
                <div className="lp-top">
                  <div className="lp-dot" style={{ background: DOT_COLORS[e.status] }} />
                  <span className="lp-name">{e.description}</span>
                  <span className="lp-bal">{fmt$(e.remaining)}</span>
                </div>
                <div className="lp-meta">
                  <span>{e.vendor}</span>
                  <span>{fmt$(e.totalCost / e.months)}/mo</span>
                  <span>{e.pct}%</span>
                </div>
                <div className="lp-bar"><div className="lp-bar-fill" style={{ width: `${e.pct}%` }} /></div>
              </div>
            ))}
            {sortedStats.length === 0 && (
              <div className="empty"><div className="empty-ico">📋</div><div style={{ fontSize: 10 }}>No schedules yet</div></div>
            )}
          </div>
          <div className="lp-nav">
            <button className="btn btn-acc btn-sm btn-block" onClick={() => { setSelectedId(null); setRightView("newEntry"); setPrefill(null); }}>+ New Entry</button>
          </div>
        </div>

        {/* ════════ RIGHT PANE ════════ */}
        <main className="main-pane">
          <div className="mp-inner">

            {/* ── BATCH RESULT (inline, persistent until dismissed) ── */}
            {batchResult && (
              <div className="batch-result">
                <div className="br-summary">
                  {batchResult.posted.length > 0 && <span className="badge b-green">✓ {batchResult.posted.length} posted</span>}
                  {batchResult.skipped.length > 0 && <span className="badge b-amber">⊘ {batchResult.skipped.length} skipped</span>}
                  {batchResult.errors.length > 0 && <span className="badge b-red">✕ {batchResult.errors.length} errors</span>}
                  <span style={{ fontSize: 10, color: "var(--acc)", fontWeight: 600 }}>
                    {fmt$(batchResult.posted.reduce((a, r) => a + r.amount, 0))} total
                  </span>
                  <span style={{ fontSize: 8, color: "var(--mu)" }}>{batchResult.batchId}</span>
                  <button className="btn btn-ghost btn-xs" style={{ marginLeft: "auto" }} onClick={() => exportEntrataBatch(batchResult)} title="Download this batch in Entrata's import format">↓ Entrata</button>
                  <button className="btn btn-ghost btn-xs" onClick={() => setBatchResult(null)}>Dismiss</button>
                </div>
                {batchResult.posted.length > 0 && (
                  <div className="br-detail">
                    {batchResult.posted.map((r) => (
                      <div key={r.key} style={{ display: "flex", gap: 8, padding: "3px 0", fontSize: 9, color: "var(--mu)", borderBottom: "1px solid var(--b1)" }}>
                        <span style={{ color: "var(--acc)", fontWeight: 600, minWidth: 70 }}>{r.jeRef}</span>
                        <span>{monthLabel(r.period)}</span>
                        <span style={{ color: "var(--acc)" }}>{fmt$(r.amount)}</span>
                        <span>Dr {r.debitGL} / Cr {r.creditGL}</span>
                        <span className="badge b-teal" style={{ fontSize: 6 }}>AmortIQ</span>
                      </div>
                    ))}
                  </div>
                )}
                {batchResult.skipped.length > 0 && (
                  <div style={{ marginTop: 6 }}>
                    {batchResult.skipped.map((r, i) => (
                      <div key={i} style={{ fontSize: 9, color: "var(--amber)", padding: "2px 0" }}>⊘ {r.key} — {r.reason}</div>
                    ))}
                  </div>
                )}
                {batchResult.errors.length > 0 && (
                  <div style={{ marginTop: 6 }}>
                    {batchResult.errors.map((r, i) => (
                      <div key={i} style={{ fontSize: 9, color: "var(--red)", padding: "2px 0" }}>✕ {r.key} — {r.reason}</div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ══════════════════════════════════════════════════════════════ */}
            {/* ── PROPERTY RECONCILIATION DASHBOARD (default view) ──────── */}
            {/* ══════════════════════════════════════════════════════════════ */}
            {rightView === "summary" && !selectedId && (
              <>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                  <span style={{ fontFamily: "var(--head)", fontSize: 17, fontWeight: 700, color: "var(--txb)" }}>
                    {monthLabel(closePeriod)} — Entity Reconciliation
                  </span>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button className="btn btn-ghost btn-sm" onClick={exportEntrataPeriod} title="Download this period's postings in Entrata's Import Journal Entries format">↓ Entrata JE</button>
                    {pendingCount > 0 && <button className="btn btn-acc btn-sm" onClick={postAllPending}>Post All Pending ({pendingCount})</button>}
                  </div>
                </div>

                {entries.length === 0 && (
                  <div className="info-box">
                    Fresh workspace — open <strong>⚙ → Setup</strong> to add your entities and GL accounts, then click <strong>+ New Entry</strong> to create your first amortization schedule.
                  </div>
                )}

                {/* Summary stats */}
                <div className="stats">
                  <div className="stat">
                    <div className="stat-val" style={{ color: "var(--acc)" }}>{fmt$(totalBalance)}</div>
                    <div className="stat-lbl">Prepaid Balance</div>
                  </div>
                  <div className="stat">
                    <div className="stat-val" style={{ color: "var(--txb)" }}>{fmt$(totalOriginalCost)}</div>
                    <div className="stat-lbl">Original Cost</div>
                  </div>
                  <div className="stat">
                    <div className="stat-val" style={{ color: "var(--txb)" }}>{fmt$(totalMonthlyRate)}</div>
                    <div className="stat-lbl">Monthly Run Rate</div>
                  </div>
                  <div className="stat">
                    <div className="stat-val">{entries.length}</div>
                    <div className="stat-lbl">Active Schedules</div>
                  </div>
                </div>

                {/* GL noise warning */}
                {externalNoiseCount > 0 && (
                  <div className="warn-box" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontWeight: 700, marginBottom: 3 }}>{externalNoiseCount} manual GL entries on prepaid accounts</div>
                      <div style={{ fontSize: 9 }}>Posted in Entrata without <span style={{ color: "var(--acc)" }}>{AMORTIQ_TAG}</span> tag. Review in GL Recon.</div>
                    </div>
                    <button className="btn btn-amber btn-sm" onClick={() => { setDrawerTab("recon"); setDrawerOpen(true); }}>Review →</button>
                  </div>
                )}

                {/* Property recon grid */}
                <div className="card" style={{ background: "transparent", border: "none", marginBottom: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 10, letterSpacing: ".12em", textTransform: "uppercase", color: "var(--acc)", fontWeight: 700 }}>By Entity</span>
                      <span className="badge b-dim">{propertyRecon.length} {propertyRecon.length === 1 ? "entity" : "entities"}</span>
                    </div>
                    <input
                      className="pr-search"
                      placeholder="Filter entities…"
                      value={propFilter}
                      onChange={(e) => setPropFilter(e.target.value)}
                    />
                  </div>

                  {/* Column headers */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 100px 120px 120px 120px 60px", padding: "6px 16px", gap: 8, borderBottom: "1px solid var(--b2)" }}>
                    <span style={{ fontSize: 9, letterSpacing: ".12em", textTransform: "uppercase", color: "var(--mu)" }}>Entity</span>
                    <span style={{ fontSize: 8, letterSpacing: ".12em", textTransform: "uppercase", color: "var(--mu)", textAlign: "right" }}>Schedules</span>
                    <span style={{ fontSize: 8, letterSpacing: ".12em", textTransform: "uppercase", color: "var(--mu)", textAlign: "right" }}>Original Cost</span>
                    <span style={{ fontSize: 8, letterSpacing: ".12em", textTransform: "uppercase", color: "var(--mu)", textAlign: "right" }}>Amortized</span>
                    <span style={{ fontSize: 8, letterSpacing: ".12em", textTransform: "uppercase", color: "var(--mu)", textAlign: "right" }}>Remaining</span>
                    <span style={{ fontSize: 8, letterSpacing: ".12em", textTransform: "uppercase", color: "var(--mu)", textAlign: "right" }}>%</span>
                  </div>

                  {/* Property rows */}
                  {filteredRecon.map((prop) => {
                    const isExpanded = expandedProp === prop.name;
                    return (
                      <div key={prop.name} className={`pr-row ${isExpanded ? "expanded" : ""}`}>
                        <div className="pr-header" onClick={() => setExpandedProp(isExpanded ? null : prop.name)}>
                          <div className="pr-name">
                            <span className="pr-arrow">▸</span>
                            {prop.name}
                          </div>
                          <div className="pr-val" style={{ color: "var(--mu)" }}>{prop.scheduleCount}</div>
                          <div className="pr-val" style={{ color: "var(--txb)" }}>{fmt$(prop.totalCost)}</div>
                          <div className="pr-val" style={{ color: "var(--acc)" }}>{fmt$(prop.amortized)}</div>
                          <div className="pr-val" style={{ color: prop.remaining > 0 ? "var(--txb)" : "var(--di)" }}>{fmt$(prop.remaining)}</div>
                          <div className="pr-pct">{prop.pct}%</div>
                        </div>
                        <div className="pr-bar">
                          <div className="pr-bar-fill" style={{ width: `${prop.pct}%` }} />
                        </div>

                        {/* Expanded detail — individual schedules for this property */}
                        {isExpanded && (
                          <div className="pr-detail">
                            <table className="tbl">
                              <thead>
                                <tr>
                                  <th></th>
                                  <th>Schedule</th>
                                  <th>Vendor</th>
                                  <th>Prepaid GL</th>
                                  <th style={{ textAlign: "right" }}>Total</th>
                                  <th style={{ textAlign: "right" }}>Monthly</th>
                                  <th style={{ textAlign: "right" }}>Amortized</th>
                                  <th style={{ textAlign: "right" }}>Remaining</th>
                                  <th style={{ textAlign: "right" }}>%</th>
                                  <th>Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {prop.entries.map((e) => (
                                  <tr key={e.id} style={{ cursor: "pointer" }} onClick={() => { setSelectedId(e.id); setRightView("detail"); }}>
                                    <td><div style={{ width: 6, height: 6, background: DOT_COLORS[e.status] }} /></td>
                                    <td style={{ fontWeight: 600, color: "var(--txb)", fontSize: 10 }}>{e.description}</td>
                                    <td style={{ color: "var(--mu)" }}>{e.vendor}</td>
                                    <td style={{ color: "var(--mu)" }}>{e.prepaidGL}</td>
                                    <td style={{ textAlign: "right", color: "var(--txb)" }}>{fmt$(e.totalCost)}</td>
                                    <td style={{ textAlign: "right", color: "var(--mu)" }}>{fmt$(e.totalCost / e.months)}</td>
                                    <td style={{ textAlign: "right", color: "var(--acc)" }}>{fmt$(e.posted)}</td>
                                    <td style={{ textAlign: "right", fontWeight: 600, color: e.remaining > 0 ? "var(--txb)" : "var(--di)" }}>{fmt$(e.remaining)}</td>
                                    <td style={{ textAlign: "right", color: "var(--mu)" }}>{e.pct}%</td>
                                    <td onClick={(ev) => ev.stopPropagation()}>
                                      <button className="btn btn-ghost btn-xs" onClick={() => openReconForEntry(e)}>GL Recon</button>
                                    </td>
                                  </tr>
                                ))}
                                {/* Property subtotal */}
                                <tr style={{ background: "var(--s2)" }}>
                                  <td colSpan={4} style={{ fontWeight: 700, fontSize: 9, color: "var(--acc)", letterSpacing: ".06em", textTransform: "uppercase" }}>
                                    {prop.name} Total
                                  </td>
                                  <td style={{ textAlign: "right", fontWeight: 700, color: "var(--txb)" }}>{fmt$(prop.totalCost)}</td>
                                  <td style={{ textAlign: "right", fontWeight: 600, color: "var(--mu)" }}>{fmt$(prop.monthlyRate)}</td>
                                  <td style={{ textAlign: "right", fontWeight: 700, color: "var(--acc)" }}>{fmt$(prop.amortized)}</td>
                                  <td style={{ textAlign: "right", fontWeight: 700, color: "var(--txb)" }}>{fmt$(prop.remaining)}</td>
                                  <td style={{ textAlign: "right", fontWeight: 700, color: "var(--mu)" }}>{prop.pct}%</td>
                                  <td></td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {filteredRecon.length === 0 && (
                    <div className="empty" style={{ padding: "24px" }}>
                      <div style={{ fontSize: 11.5 }}>No entities match filter</div>
                    </div>
                  )}

                  {/* Grand total row */}
                  {filteredRecon.length > 0 && (
                    <div className="pr-totals">
                      <div style={{ fontSize: 9, color: "var(--acc)", letterSpacing: ".06em", textTransform: "uppercase" }}>
                        Grand Total — {propertyRecon.length} {propertyRecon.length === 1 ? "entity" : "entities"}
                      </div>
                      <div style={{ textAlign: "right", color: "var(--mu)", fontSize: 11 }}>{entries.length}</div>
                      <div style={{ textAlign: "right", color: "var(--txb)", fontSize: 11 }}>{fmt$(totalOriginalCost)}</div>
                      <div style={{ textAlign: "right", color: "var(--acc)", fontSize: 11 }}>{fmt$(totalAmortized)}</div>
                      <div style={{ textAlign: "right", color: "var(--txb)", fontSize: 11 }}>{fmt$(totalBalance)}</div>
                      <div style={{ textAlign: "right", color: "var(--mu)", fontSize: 9 }}>{totalOriginalCost > 0 ? Math.round((totalAmortized / totalOriginalCost) * 100) : 0}%</div>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* ── SCHEDULE DETAIL ── */}
            {rightView === "detail" && selectedEntry && (() => {
              const es = entryStats.find((e) => e.id === selectedEntry.id);
              const zones = getZones(selectedEntry);
              return (
                <>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontFamily: "var(--head)", fontSize: 16, fontWeight: 700, color: "var(--txb)" }}>{selectedEntry.description}</span>
                      <span className="badge b-teal">{selectedEntry.vendor}</span>
                      {es?.status === "behind" && <span className="badge b-red">Behind</span>}
                      {es?.status === "pending" && <span className="badge b-amber">Pending</span>}
                      {es?.fullyAmortized && <span className="badge b-dim">Complete</span>}
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => exportScheduleCSV(selectedEntry, ledger)}>↓ CSV</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => duplicateEntry(selectedEntry)}>Duplicate</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => openReconForEntry(selectedEntry)}>GL Recon</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => { setSelectedId(null); setRightView("summary"); }}>← Back</button>
                    </div>
                  </div>

                  {/* Metadata strip */}
                  <div className="kv-strip">
                    {[
                      ["Total", fmt$(selectedEntry.totalCost), "var(--txb)"],
                      ["Monthly", fmt$(selectedEntry.totalCost / selectedEntry.months), "var(--acc)"],
                      ["Remaining", fmt$(es?.remaining), "var(--amber)"],
                      ["Term", `${selectedEntry.months} mo`, "var(--txb)"],
                      ["Prepaid GL", selectedEntry.prepaidGL, "var(--acc)"],
                      ["Expense GL", selectedEntry.expenseGL, "var(--mu)"],
                      ["Entity", selectedEntry.property, "var(--txb)"],
                      ["Invoice", selectedEntry.invoiceRef || "—", "var(--mu)"],
                      ["By", selectedEntry.enteredBy || "—", "var(--mu)"],
                    ].map(([l, v, c]) => (
                      <div key={l} className="kv"><div className="kv-label">{l}</div><div className="kv-val" style={{ color: c }}>{v}</div></div>
                    ))}
                  </div>

                  {/* Progress */}
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                    <div className="prog" style={{ flex: 1 }}><div className="prog-fill" style={{ width: `${es?.pct || 0}%` }} /></div>
                    <span style={{ fontSize: 10, color: "var(--mu)" }}>{es?.pct}% amortized</span>
                    {(es?.hasCurrent || es?.hasBehind) && !es?.fullyAmortized && (
                      <button className="btn btn-acc btn-sm" onClick={() => postEntryAll(selectedEntry.id)}>Post All Pending</button>
                    )}
                  </div>

                  {/* ── ZONE: Behind (past unposted) ── */}
                  {zones.behind.length > 0 && (
                    <div className="zone zone-behind">
                      <div className="zone-hd">
                        <span className="badge b-red">Behind</span>
                        <span className="zone-label" style={{ color: "var(--red)" }}>{zones.behind.length} unposted prior periods</span>
                        <span className="zone-count">{fmt$(zones.behind.reduce((a, r) => a + r.amount, 0))}</span>
                      </div>
                      <div className="zone-body">
                        <table className="tbl">
                          <thead><tr><th>Period</th><th>Dr GL</th><th>Cr GL</th><th style={{ textAlign: "right" }}>Amount</th><th>Action</th></tr></thead>
                          <tbody>{zones.behind.map((row) => (
                            <tr key={row.rowId}>
                              <td style={{ fontWeight: 600, color: "var(--red)" }}>{monthLabel(row.period)}</td>
                              <td style={{ fontSize: 9, color: "var(--mu)" }}>{row.debitGL}</td>
                              <td style={{ fontSize: 9, color: "var(--mu)" }}>{row.creditGL}</td>
                              <td style={{ textAlign: "right", fontWeight: 600, color: "var(--acc)" }}>{fmt$(row.amount)}</td>
                              <td>
                                {recentlyPosted.has(row.postingKey)
                                  ? <span className="inline-ok">✓ Posted</span>
                                  : <button className="btn btn-acc btn-xs" onClick={() => postSingle(row.postingKey)}>Post</button>}
                              </td>
                            </tr>
                          ))}</tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* ── ZONE: Current period ── */}
                  {zones.current.length > 0 && (
                    <div className="zone zone-action">
                      <div className="zone-hd">
                        <span className="badge b-teal">Current</span>
                        <span className="zone-label" style={{ color: "var(--acc)" }}>{monthLabel(closePeriod)}</span>
                        <span className="zone-count">{fmt$(zones.current.reduce((a, r) => a + r.amount, 0))}</span>
                      </div>
                      <div className="zone-body">
                        <table className="tbl">
                          <thead><tr><th>Period</th><th>Dr GL</th><th>Cr GL</th><th style={{ textAlign: "right" }}>Amount</th><th>JE Ref</th><th>ERP Memo</th><th>Action</th></tr></thead>
                          <tbody>{zones.current.map((row) => {
                            const rec = ledger[row.postingKey];
                            return (
                              <tr key={row.rowId}>
                                <td style={{ fontWeight: 600, color: "var(--acc)" }}>{monthLabel(row.period)}</td>
                                <td style={{ fontSize: 9, color: "var(--mu)" }}>{row.debitGL}</td>
                                <td style={{ fontSize: 9, color: "var(--mu)" }}>{row.creditGL}</td>
                                <td style={{ textAlign: "right", fontWeight: 600, color: "var(--acc)" }}>{fmt$(row.amount)}</td>
                                <td style={{ fontSize: 9 }}>{rec ? <span style={{ color: "var(--acc)", fontWeight: 600 }}>{rec.jeRef}</span> : "—"}</td>
                                <td style={{ fontSize: 8, color: rec ? "var(--acc)" : "var(--di)", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>
                                  {rec ? rec.memo : `Will be: ${AMORTIQ_TAG} ${row.memo}`}
                                </td>
                                <td>
                                  {recentlyPosted.has(row.postingKey) ? <span className="inline-ok">✓ Posted</span>
                                    : rec ? <button className="btn btn-danger btn-xs" onClick={() => { setRevTarget(rec); setRevReason(""); }}>Reverse</button>
                                    : <button className="btn btn-acc btn-xs" onClick={() => postSingle(row.postingKey)}>Post</button>}
                                </td>
                              </tr>
                            );
                          })}</tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* ── ZONE: Past posted (collapsed by default) ── */}
                  {zones.pastPosted.length > 0 && (
                    <div className="zone zone-past">
                      <div className="zone-hd" onClick={() => setShowPastZone((v) => !v)}>
                        <span className="badge b-dim">Completed</span>
                        <span className="zone-label" style={{ color: "var(--mu)" }}>{zones.pastPosted.length} posted periods</span>
                        <span className="zone-count">{showPastZone ? "▾" : "▸"} {fmt$(zones.pastPosted.reduce((a, r) => a + r.amount, 0))}</span>
                      </div>
                      {showPastZone && (
                        <div className="zone-body">
                          <table className="tbl">
                            <thead><tr><th>Period</th><th>Dr GL</th><th>Cr GL</th><th style={{ textAlign: "right" }}>Amount</th><th>JE Ref</th><th>Batch</th><th>Action</th></tr></thead>
                            <tbody>{zones.pastPosted.map((row) => {
                              const rec = ledger[row.postingKey];
                              return (
                                <tr key={row.rowId}>
                                  <td>{monthLabel(row.period)}</td>
                                  <td style={{ fontSize: 9, color: "var(--mu)" }}>{row.debitGL}</td>
                                  <td style={{ fontSize: 9, color: "var(--mu)" }}>{row.creditGL}</td>
                                  <td style={{ textAlign: "right" }}>{fmt$(row.amount)}</td>
                                  <td style={{ fontSize: 9, color: "var(--acc)" }}>{rec?.jeRef}</td>
                                  <td style={{ fontSize: 8, color: "var(--mu)" }}>{rec?.batchId}</td>
                                  <td><button className="btn btn-danger btn-xs" onClick={() => { setRevTarget(rec); setRevReason(""); }}>Reverse</button></td>
                                </tr>
                              );
                            })}</tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── ZONE: Future ── */}
                  {zones.future.length > 0 && (
                    <div className="zone zone-future">
                      <div className="zone-hd" onClick={() => setShowFutureZone((v) => !v)}>
                        <span className="badge b-dim">Upcoming</span>
                        <span className="zone-label" style={{ color: "var(--mu)" }}>{zones.future.length} future periods</span>
                        <span className="zone-count">{showFutureZone ? "▾" : "▸"} {fmt$(zones.future.reduce((a, r) => a + r.amount, 0))}</span>
                      </div>
                      {showFutureZone && (
                        <div className="zone-body">
                          <table className="tbl">
                            <thead><tr><th>Period</th><th>Dr GL</th><th>Cr GL</th><th style={{ textAlign: "right" }}>Amount</th><th>Scheduled Memo</th></tr></thead>
                            <tbody>{zones.future.map((row) => (
                              <tr key={row.rowId}>
                                <td style={{ color: "var(--mu)" }}>{monthLabel(row.period)}</td>
                                <td style={{ fontSize: 9, color: "var(--di)" }}>{row.debitGL}</td>
                                <td style={{ fontSize: 9, color: "var(--di)" }}>{row.creditGL}</td>
                                <td style={{ textAlign: "right", color: "var(--mu)" }}>{fmt$(row.amount)}</td>
                                <td style={{ fontSize: 8, color: "var(--di)" }}>{AMORTIQ_TAG} {row.memo}</td>
                              </tr>
                            ))}</tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </>
              );
            })()}

            {/* ── NEW ENTRY FORM ── */}
            {rightView === "newEntry" && (
              <NewEntryForm prefill={prefill} onSave={addEntry} onCancel={() => { setRightView("summary"); setPrefill(null); }} glAccounts={glAccounts} entities={entities} />
            )}

          </div>
        </main>

        {/* ════════ DRAWER — GL RECON + AUDIT LOG ════════ */}
        <div className={`drawer-overlay ${drawerOpen ? "open" : ""}`} onClick={() => setDrawerOpen(false)} />
        <div className={`drawer ${drawerOpen ? "open" : ""}`}>
          <div className="drawer-hd">
            <span className="drawer-title">Analysis & Audit</span>
            <button className="close-btn" onClick={() => setDrawerOpen(false)}>×</button>
          </div>
          <div className="drawer-tabs">
            <button className={`dtab ${drawerTab === "recon" ? "on" : ""}`} onClick={() => setDrawerTab("recon")}>GL Reconciliation</button>
            <button className={`dtab ${drawerTab === "audit" ? "on" : ""}`} onClick={() => setDrawerTab("audit")}>Audit Log ({auditLog.length})</button>
            <button className={`dtab ${drawerTab === "setup" ? "on" : ""}`} onClick={() => setDrawerTab("setup")}>Setup</button>
          </div>
          <div className="drawer-body">

            {/* ── GL RECON ── */}
            {drawerTab === "recon" && (() => {
              const glOptions = glAccounts.filter((g) => g.category === "prepaid_asset");
              const piqPostings = Object.values(ledger).filter((r) => r.creditGL === drawerGL || r.debitGL === drawerGL);
              const externalActivity = NOISE.filter((r) => r.glCode === drawerGL);
              const relatedEntries = entries.filter((e) => e.prepaidGL === drawerGL);
              const scheduledBalance = relatedEntries.reduce((a, e) => {
                const posted = e.schedule.filter((r) => ledger[r.postingKey]).reduce((x, r) => x + r.amount, 0);
                return a + (e.totalCost - posted);
              }, 0);
              const totalPiqAmort = piqPostings.reduce((a, r) => a + (r.creditGL === drawerGL ? r.amount : -r.amount), 0);
              const totalExternal = externalActivity.reduce((a, r) => a + (r.debitCredit === "CR" ? -Math.abs(r.amount) : Math.abs(r.amount)), 0);
              const totalOrigCost = relatedEntries.reduce((a, e) => a + e.totalCost, 0);
              const simulatedGLBalance = totalOrigCost - totalPiqAmort + totalExternal;
              const variance = simulatedGLBalance - scheduledBalance;
              const glName = glAccounts.find((g) => g.code === drawerGL)?.name || drawerGL;
              const hasNoise = externalActivity.length > 0;

              return (
                <>
                  <div className="field" style={{ maxWidth: 320 }}>
                    <label className="lbl">Prepaid GL Account</label>
                    <select className="sel" value={drawerGL} onChange={(e) => setDrawerGL(e.target.value)}>
                      {glOptions.map((g) => <option key={g.code} value={g.code}>{g.code} · {g.name}</option>)}
                    </select>
                  </div>

                  {/* Summary cards */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 14 }}>
                    {[
                      ["Schedule Bal", fmt$(scheduledBalance), "var(--acc)"],
                      ["GL Balance", fmt$(simulatedGLBalance), "var(--txb)"],
                      ["Variance", fmt$(variance), Math.abs(variance) < 0.01 ? "var(--acc)" : "var(--red)"],
                    ].map(([l, v, c]) => (
                      <div key={l} style={{ border: "1px solid var(--b1)", padding: "8px 10px", background: "var(--s1)" }}>
                        <div style={{ fontSize: 7, color: "var(--mu)", letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 2 }}>{l}</div>
                        <div style={{ fontSize: 16, fontFamily: "var(--head)", fontWeight: 900, color: c }}>{v}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
                    <div style={{ border: "1px solid var(--b1)", padding: "8px 10px" }}>
                      <div style={{ fontSize: 7, color: "var(--mu)", letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 2 }}>AmortIQ Postings</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "var(--acc)" }}>{piqPostings.length} <span style={{ fontSize: 9, color: "var(--mu)", fontWeight: 400 }}>({fmt$(totalPiqAmort)})</span></div>
                    </div>
                    <div style={{ border: "1px solid var(--b1)", padding: "8px 10px" }}>
                      <div style={{ fontSize: 7, color: "var(--mu)", letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 2 }}>External Activity</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: hasNoise ? "var(--amber)" : "var(--acc)" }}>{externalActivity.length} <span style={{ fontSize: 9, color: "var(--mu)", fontWeight: 400 }}>{hasNoise ? fmt$(Math.abs(totalExternal)) : "Clean"}</span></div>
                    </div>
                  </div>

                  {Math.abs(variance) >= 0.01 && hasNoise && (
                    <div className="warn-box">
                      <strong>{externalActivity.length} manual {externalActivity.length === 1 ? "entry" : "entries"}</strong> on {drawerGL} · {glName} — posted without <span style={{ color: "var(--acc)" }}>{AMORTIQ_TAG}</span> tag.
                    </div>
                  )}

                  {/* AmortIQ postings */}
                  <div className="recon-panel">
                    <div className="recon-hd" style={{ color: "var(--acc)" }}>
                      <span className="badge b-teal">AmortIQ</span> Tagged Postings <span style={{ marginLeft: "auto", fontSize: 8, color: "var(--mu)" }}>{piqPostings.length}</span>
                    </div>
                    <div className="recon-body">
                      {piqPostings.length === 0 ? (
                        <div style={{ padding: "16px", textAlign: "center", fontSize: 9, color: "var(--mu)" }}>No postings on this GL yet</div>
                      ) : piqPostings.slice(0, 20).map((r) => (
                        <div key={r.key} className="recon-row">
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                            <span style={{ color: "var(--acc)", fontWeight: 600 }}>{r.jeRef}</span>
                            <span style={{ color: "var(--acc)", fontWeight: 600 }}>{fmt$(r.amount)}</span>
                          </div>
                          <div style={{ color: "var(--mu)", fontSize: 8 }}>{monthLabel(r.period)} · {r.postedBy} · {r.batchId}</div>
                          <div className="recon-memo" style={{ color: "var(--acc)", borderColor: "var(--acc)" }}>{r.memo}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* External / manual entries */}
                  <div className="recon-panel" style={{ borderColor: hasNoise ? "var(--amberBd)" : "var(--b1)" }}>
                    <div className="recon-hd" style={{ color: hasNoise ? "var(--amber)" : "var(--mu)" }}>
                      <span className={`badge ${hasNoise ? "b-orange" : "b-dim"}`}>Manual</span> External GL Activity <span style={{ marginLeft: "auto", fontSize: 8, color: "var(--mu)" }}>{externalActivity.length}</span>
                    </div>
                    <div className="recon-body">
                      {externalActivity.length === 0 ? (
                        <div style={{ padding: "16px", textAlign: "center" }}>
                          <div style={{ fontSize: 14, marginBottom: 4 }}>✓</div>
                          <div style={{ fontSize: 9, color: "var(--acc)", fontWeight: 600 }}>Clean GL — no manual entries</div>
                        </div>
                      ) : externalActivity.map((r) => (
                        <div key={r.glJeRef} className="recon-row">
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                              <span style={{ color: "var(--amber)", fontWeight: 600 }}>{r.glJeRef}</span>
                              <span className="badge b-orange">No Tag</span>
                            </div>
                            <span style={{ color: r.debitCredit === "CR" ? "var(--red)" : "var(--acc)", fontWeight: 600 }}>
                              {r.debitCredit === "CR" ? "−" : "+"}{fmt$(Math.abs(r.amount))}
                            </span>
                          </div>
                          <div style={{ color: "var(--mu)", fontSize: 8 }}>{monthLabel(r.period)} · {r.postedBy}</div>
                          <div className="recon-memo" style={{ color: "var(--amber)", borderColor: "var(--amber)" }}>{r.memo}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="info-box">
                    <strong>Source tagging:</strong> AmortIQ prefixes every JE memo with <strong>{AMORTIQ_TAG}</strong>. On GL read, entries with the tag are ours; entries without are external noise requiring review.
                  </div>
                </>
              );
            })()}

            {/* ── AUDIT LOG ── */}
            {drawerTab === "audit" && (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <span style={{ fontSize: 10, color: "var(--mu)" }}>Append-only log · {auditLog.length} entries</span>
                  <button className="btn btn-ghost btn-sm" onClick={() => exportAuditCSV(auditLog)}>↓ CSV</button>
                </div>
                <div style={{ border: "1px solid var(--b1)" }}>
                  <table className="tbl">
                    <thead><tr><th>Time</th><th>Action</th><th>Key</th><th>By</th><th>Note</th></tr></thead>
                    <tbody>
                      {[...auditLog].reverse().slice(0, 100).map((r, i) => {
                        const ac = { POST: "b-teal", SKIP: "b-amber", REVERSE: "b-red" };
                        return (
                          <tr key={i}>
                            <td style={{ fontSize: 8, color: "var(--mu)", whiteSpace: "nowrap" }}>{r.ts.slice(0, 19).replace("T", " ")}</td>
                            <td><span className={`badge ${ac[r.action] || "b-dim"}`}>{r.action}</span></td>
                            <td style={{ fontSize: 8, color: "var(--mu)", maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis" }}>{r.key}</td>
                            <td style={{ fontSize: 9, color: "var(--mu)" }}>{r.by}</td>
                            <td style={{ fontSize: 8, color: "var(--mu)", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>{r.note}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {/* ── SETUP ── */}
            {drawerTab === "setup" && (
              <>
                <div className="card">
                  <div className="card-hd"><span className="card-title">Entities ({entities.length})</span></div>
                  <div className="card-body">
                    {entities.length === 0 && <div style={{ fontSize: 11.5, color: "var(--mu)", marginBottom: 10 }}>Add the properties/companies you track — schedules attach to these.</div>}
                    {entities.length > 0 && (
                      <table className="tbl" style={{ marginBottom: 12 }}>
                        <thead><tr><th>Name</th><th>Type</th></tr></thead>
                        <tbody>{entities.map((e) => (<tr key={e.id}><td style={{ fontWeight: 600, color: "var(--txb)" }}>{e.name}</td><td style={{ color: "var(--mu)" }}>{e.entity_type}</td></tr>))}</tbody>
                      </table>
                    )}
                    <div style={{ display: "flex", gap: 8 }}>
                      <input className="inp" style={{ flex: 2 }} placeholder="Entity name" value={setupEnt.name} onChange={(e) => setSetupEnt((p) => ({ ...p, name: e.target.value }))} />
                      <select className="sel" style={{ flex: 1 }} value={setupEnt.type} onChange={(e) => setSetupEnt((p) => ({ ...p, type: e.target.value }))}>
                        <option value="property">Property</option>
                        <option value="corporate">Corporate</option>
                        <option value="affiliate">Affiliate</option>
                        <option value="other">Other</option>
                      </select>
                      <button className="btn btn-acc btn-sm" disabled={!setupEnt.name.trim()} onClick={async () => { if (await addEntity(setupEnt.name, setupEnt.type)) setSetupEnt({ name: "", type: "property" }); }}>Add</button>
                    </div>
                  </div>
                </div>
                <div className="card">
                  <div className="card-hd"><span className="card-title">GL Accounts ({glAccounts.length})</span></div>
                  <div className="card-body">
                    {glAccounts.length === 0 && <div style={{ fontSize: 11.5, color: "var(--mu)", marginBottom: 10 }}>Add at least one prepaid asset GL and one expense GL to create schedules.</div>}
                    {glAccounts.length > 0 && (
                      <table className="tbl" style={{ marginBottom: 12 }}>
                        <thead><tr><th>Code</th><th>Name</th><th>Category</th></tr></thead>
                        <tbody>{glAccounts.map((g) => (<tr key={g.id}><td style={{ fontFamily: "var(--mono)" }}>{g.code}</td><td style={{ fontWeight: 600, color: "var(--txb)" }}>{g.name}</td><td style={{ color: "var(--mu)" }}>{g.category.replace(/_/g, " ")}</td></tr>))}</tbody>
                      </table>
                    )}
                    <div style={{ display: "flex", gap: 8 }}>
                      <input className="inp" style={{ flex: 1 }} placeholder="Code" value={setupGl.code} onChange={(e) => setSetupGl((p) => ({ ...p, code: e.target.value }))} />
                      <input className="inp" style={{ flex: 2 }} placeholder="Account name" value={setupGl.name} onChange={(e) => setSetupGl((p) => ({ ...p, name: e.target.value }))} />
                      <select className="sel" style={{ flex: 1.4 }} value={setupGl.category} onChange={(e) => setSetupGl((p) => ({ ...p, category: e.target.value }))}>
                        <option value="prepaid_asset">Prepaid asset</option>
                        <option value="expense">Expense</option>
                        <option value="ic_due_to">IC due to</option>
                        <option value="ic_due_from">IC due from</option>
                        <option value="accounts_payable">Accounts payable</option>
                        <option value="other">Other</option>
                      </select>
                      <button className="btn btn-acc btn-sm" disabled={!setupGl.code.trim() || !setupGl.name.trim()} onClick={async () => { if (await addGlAccount(setupGl.code, setupGl.name, setupGl.category)) setSetupGl({ code: "", name: "", category: "prepaid_asset" }); }}>Add</button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* ════════ REVERSAL MODAL ════════ */}
        {revTarget && (
          <div className="modal-overlay" onClick={() => setRevTarget(null)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div style={{ fontFamily: "var(--head)", fontSize: 15, fontWeight: 700, color: "var(--red)", marginBottom: 6 }}>Reverse Posted Entry</div>
              <div style={{ fontSize: 10, color: "var(--mu)", lineHeight: 1.6, marginBottom: 12 }}>
                Removes this posting key from the ledger and unlocks the period for re-posting. The reversal JE will also carry an <span style={{ color: "var(--acc)" }}>{AMORTIQ_TAG}</span> tag.
              </div>
              <div style={{ border: "1px solid var(--redBd)", background: "var(--redBg)", padding: "10px 12px", marginBottom: 12, fontSize: 10 }}>
                <div style={{ color: "var(--red)", fontWeight: 700, marginBottom: 3 }}>{revTarget.jeRef} · {monthLabel(revTarget.period)} · {fmt$(revTarget.amount)}</div>
                <div style={{ color: "var(--mu)", fontSize: 9 }}>Dr {revTarget.debitGL} / Cr {revTarget.creditGL}</div>
                <div style={{ color: "var(--acc)", fontSize: 8, marginTop: 4 }}>{revTarget.memo}</div>
              </div>
              <div className="field">
                <label className="lbl">Reason for reversal (required)</label>
                <textarea className="inp" value={revReason} onChange={(e) => setRevReason(e.target.value)} placeholder="e.g. Transaction reclassified — new prepaid entry added" />
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
                <button className="btn btn-ghost" onClick={() => setRevTarget(null)}>Cancel</button>
                <button className="btn btn-danger" disabled={!revReason.trim()} onClick={confirmReversal}>Confirm Reversal</button>
              </div>
            </div>
          </div>
        )}

        {flash && <div className="flash">{flash}</div>}
      </div>
    </>
  );
}
